import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { PriceComponentType } from '../../api-client/enums'
import { makeOfferByBuyer } from '../../api-client/listing'
import { Button } from '../button'
import { PriceFormField } from '../inputs'
import Modal from '../modal'

const priceOptions = {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}

export default function MakeAnOfferModal({
  vehicleId,
  buyNowListingId,
  minPrice,
  buyNowPrice,
  onSuccess,
  onClose,
  shipmentRate,
}) {
  const [amount, setAmount] = useState('')
  const [error, setError] = useState(null)
  const [acceptedPrices, setAcceptedPrices] = useState([])

  const { mutate, isLoading } = useMutation({
    mutationFn: makeOfferByBuyer,
    onSuccess,
    onError: ({ errors }) => setError(errors[0]?.message || 'Something went wrong'),
  })

  const buyFee = Number(process.env.NEXT_PUBLIC_BUY_FEE)

  useEffect(() => {
    const shippingFee = shipmentRate?.isSucceeded ? shipmentRate.rate : 0
    setAcceptedPrices([
      { amount: amount, type: PriceComponentType.VehiclePrice },
      { amount: buyFee, type: PriceComponentType.ServiceFee },
      { amount: shippingFee, type: PriceComponentType.ShippingFee },
    ])
  }, [shipmentRate, amount, buyFee])

  const handlePriceChange = (event) => {
    setAmount(event.target.value)
    if (error) setError(null)
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    if (amount) {
      if (+amount > buyNowPrice) {
        setError(
          'This offer exceeds the Buy it Now price. You can buy the vehicle for that price or make a lower offer.',
        )
      } else if (+amount < minPrice) {
        setError(
          `Minimum offer accepted by the buyer is ${minPrice.toLocaleString(
            'en-US',
            priceOptions,
          )}, please enter same or higher price.`,
        )
      } else mutate({ buyNowListingId, offerAmount: +amount, acceptedPrices })
    } else {
      setError('Enter the amount')
    }
  }

  return (
    <Modal onClose={onClose}>
      <div className='wrapper'>
        <h2 className='title'>Make an offer</h2>
        <p className='description'>
          Making a direct offer will open negotiations between you and the seller. Both you and the
          seller will have a limit of 3 counter-offers.
          <br />
          <br />
          If an offer is accepted, you will begin the purchase process and the vehicle will be sent
          to the concierge.
        </p>
        <form onSubmit={handleSubmit}>
          <fieldset>
            <label htmlFor='offerAmount'>Enter your offer</label>
            <PriceFormField
              id='offerAmount'
              autoComplete='off'
              disabled={isLoading}
              error={!!error}
              value={amount}
              onChange={handlePriceChange}
            />
            {error && <p className='error'>{error}</p>}
            <p className='hint'>
              This vehicle has a minimum offer enabled. If your offer is too low the seller will not
              be notified of your offer.
            </p>
          </fieldset>
          <div className='buttonsContainer flex justifyCenter'>
            <Button color='secondary' onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={isLoading} type='submit'>
              Offer
            </Button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .wrapper {
          max-width: 92vw;
          width: 43.6rem;
          min-height: 46.9rem;
          padding: 2.6rem 2.4rem 4.4rem;
          border-radius: 1rem;
          background: #fff;
        }

        .title {
          text-align: center;
          font-size: 3.2rem;
        }

        .description {
          margin: 0.7rem 0 2rem;
          text-align: center;
          font-size: 1.6rem;
        }

        fieldset {
          label {
            display: block;
            margin: 0 0 0.8rem;
            text-align: center;
            font-size: 1.6rem;
          }

          .error {
            margin: 0.7rem 0 0;
            text-align: center;
            color: var(--alert-color);
            font-size: 1.4rem;
            font-weight: 700;
          }

          .hint {
            margin: 2rem 0 0;
            text-align: center;
            color: var(--secondary-text-color);
            font-size: 1.4rem;
          }
        }

        .buttonsContainer {
          margin: 2rem 0 0;

          :global(button) {
            width: 14.4rem;
            margin: 0 0.35rem;
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            max-width: 100%;
            width: 100%;
            height: 100%;
            padding: 5rem 1rem 0;
          }

          .title {
            font-size: 1.8rem;
          }

          .description {
            margin: 0.5rem 0 2.8rem;
            padding: 0 1.5rem;
            font-size: 1.4rem;
          }

          fieldset {
            label {
              font-size: 1.4rem;
            }

            .hint {
              margin: 1.3rem 0 0;
              font-style: italic;
            }
          }

          form {
            .buttonsContainer {
              position: fixed;
              left: 0;
              bottom: 2.1rem;
              z-index: 1;
              width: 100%;
              margin: 0;
              padding: 0 1.3rem;

              :global(button) {
                width: 100%;
              }
            }
          }
        }
      `}</style>
    </Modal>
  )
}
